import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { injectIntl, intlShape } from "@ai360/core/intl";
import {
    Bucket,
    BucketHeader,
    Checkbox,
    Loader,
    NumericInput,
    SelectInput,
    TextInput,
} from "~/core";
import * as actions from "./actions";
import { IErrorCodeList } from "./interfaces";
import * as selectors from "./selectors";
import { ImageryIcon } from "~/core/icons/imagery";
import {
    actions as picklistActions,
    picklistNames,
    selectors as picklistSelectors,
} from "~/core/picklist";
import { PicklistAPI } from "@ai360/core";

import "./analysis-imagery-setup-form.css";
import "./analysis-info.css";
import "~/action-panel/components/common/rec-event-info/rec-event-info.css";
import {
    ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET,
    ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL,
    IAnalysisImagerySetupSummary,
} from "~/recs-events/analysis/model";
import { messages } from "../i18n-messages";
import _ from "lodash";
import { zonesHaveBeenAdded } from "~/recs-events/selectors";
import { getToolsetPayload } from "~/map/components/map-tools/selectors";
import { useLoginUserPreferences } from "~/core/components/login-user-preferences";

class IAnalysisImagerySetupProps {
    analysisSaveErrorMessages: string[];
    analysisSummary: IAnalysisImagerySetupSummary;
    croppingSeasonOptions: PicklistAPI.IPicklistItem[];
    zonesAdded: boolean;
    fieldGuid: string;
    fetchPicklistData: (picklists) => void;
    imageryTypeOptions: PicklistAPI.IPicklistItem[];
    intl: intlShape;
    updateAnalysisSummary: (newProps) => void;
}

export const formLabelMessage = messages.imagerySidebar;
export const formLabelIcon = ImageryIcon;

const SATELLITE_IMAGERY_SETUP = "SatelliteImagerySetup";
const MAX_CLOUD_PCT = "maxCloudPct";
const NUM_CLASSES = "numClasses";
const numberOfClasses = _.range(2, 16);

const errorCodeToMessageIdSetMap = new Map([
    [2805, messages.analysisMethodPlaceholderText],
    [2806, messages.analysisDuplicateName],
]);

const monthNumbers = [
    [1, "January"],
    [2, "February"],
    [3, "March"],
    [4, "April"],
    [5, "May"],
    [6, "June"],
    [7, "July"],
    [8, "August"],
    [9, "September"],
    [10, "October"],
    [11, "November"],
    [12, "December"],
];

export const errorCodesApply = (errorCodeList: IErrorCodeList): number => {
    return errorCodeList.some((errorCode) => errorCodeToMessageIdSetMap.has(errorCode));
};

const AnalysisImagerySetupForm = (props: IAnalysisImagerySetupProps): JSX.Element => {
    const {
        analysisSaveErrorMessages,
        analysisSummary,
        croppingSeasonOptions,
        zonesAdded,
        fetchPicklistData,
        imageryTypeOptions,
        updateAnalysisSummary,
    } = props;
    const { formatMessage } = props.intl;

    //state variables

    const [initialMaxClouds, setInitialMaxClouds] = useState<number>(0);
    const [initialNumClasses, setInitialNumClasses] = useState<string>("5");

    //Fetch Preferences

    const { loginUserPreferences: maxCloudPctPrefs, isLoading: isLoadingMaxCloud } =
        useLoginUserPreferences(SATELLITE_IMAGERY_SETUP, [MAX_CLOUD_PCT]);

    const { loginUserPreferences: numClassesPrefs, isLoading: isLoadingNumClasses } =
        useLoginUserPreferences(SATELLITE_IMAGERY_SETUP, [NUM_CLASSES]);

    const loginMaxCloudPct = isLoadingMaxCloud
        ? null
        : maxCloudPctPrefs?.maxCloudPct
        ? maxCloudPctPrefs.maxCloudPct
        : 50;

    const loginNumClasses = isLoadingNumClasses
        ? null
        : numClassesPrefs?.numClasses
        ? numClassesPrefs.numClasses
        : 5;

    const userMaxCloudPref = !analysisSummary.isNew
        ? initialMaxClouds
        : loginMaxCloudPct
        ? Number(loginMaxCloudPct)
        : null;

    const userNumClassesPref = !analysisSummary.isNew
        ? initialNumClasses
        : loginNumClasses
        ? Number(loginNumClasses)
        : null;

    const updateImagerySetup = useCallback(
        (data) => {
            updateAnalysisSummary({ ...data, hasChanged: true });
        },
        [updateAnalysisSummary]
    );

    useEffect(() => {
        fetchPicklistData({
            [picklistNames.PICKLIST_CROPPING_SEASON]: picklistNames.getPickListCode(
                picklistNames.PICKLIST_CROPPING_SEASON
            ),
            [picklistNames.PICKLIST_IMAGERY_TYPE]: picklistNames.getPickListCode(
                picklistNames.PICKLIST_IMAGERY_TYPE
            ),
        });
    }, [fetchPicklistData]);

    useEffect(() => {
        setInitialMaxClouds(analysisSummary.maximumPercentCloudsAllowed);
    }, [analysisSummary.maximumPercentCloudsAllowed]);

    useEffect(() => {
        setInitialNumClasses(analysisSummary.numClasses);
    }, [analysisSummary.numClasses]);

    useEffect(() => {
        if (!analysisSummary.maximumPercentCloudsAllowed && userMaxCloudPref != null) {
            updateImagerySetup({ maximumPercentCloudsAllowed: userMaxCloudPref });
        }
    }, [analysisSummary.maximumPercentCloudsAllowed, updateImagerySetup, userMaxCloudPref]);

    useEffect(() => {
        if (analysisSummary.isNew && userNumClassesPref != null) {
            updateImagerySetup({ numClasses: userNumClassesPref });
        }
    }, [analysisSummary.isNew, updateImagerySetup, userNumClassesPref]);

    useEffect(() => {
        updateImagerySetup({ zonesAdded: zonesAdded });
    }, [updateImagerySetup, zonesAdded]);

    const updateSelectedImageryTypes = (imageryTypeGuid: string, value: boolean) => {
        let newSelectedList = analysisSummary.imageryTypeGuidList || [];
        if (value) {
            newSelectedList.push(imageryTypeGuid);
        } else {
            newSelectedList = newSelectedList.filter((i) => i !== imageryTypeGuid);
        }
        updateImagerySetup({ imageryTypeGuidList: newSelectedList });
    };

    const getTypeCheckboxes = () => {
        const checkboxes = [];
        let setupType;
        switch (analysisSummary.analysisLayerType) {
            case ANALYSIS_INFO_NAME_IMAGERY_SETUP_PLANET:
                setupType = "Planet";
                break;
            case ANALYSIS_INFO_NAME_IMAGERY_SETUP_SENTINEL:
                setupType = "Sentinel 2";
                break;
        }
        const filteredImageryTypeOptions = imageryTypeOptions.filter(
            (option) => option.activeYn === true && option.label.includes(setupType)
        );
        filteredImageryTypeOptions.forEach((option) => {
            checkboxes.push(
                <Checkbox
                    className="imagery-setup-checkbox"
                    key={option.value}
                    label={option.label}
                    value={analysisSummary.imageryTypeGuidList?.includes(option.value as string)}
                    onChange={(evt, v) => updateSelectedImageryTypes(option.value as string, v)}
                />
            );
        });
        return checkboxes;
    };

    const isNdviEnabled = () => {
        const filteredImageryTypeOptions = imageryTypeOptions.filter(
            (option) => option.activeYn === true && option.label.includes("NDVI")
        );

        const ndviGuids = filteredImageryTypeOptions.map((option) => option.value);
        return analysisSummary.imageryTypeGuidList?.some((g) => ndviGuids.includes(g));
    };

    const isStandardizedLegendChecked = () => {
        return analysisSummary.calculateAbsoluteNdvi.valueOf();
    };

    const getClassificationOptions = () => {
        return (
            <Checkbox
                className="imagery-setup-checkbox"
                disabled={!isNdviEnabled()}
                label={formatMessage(messages.standardizedLegend)}
                value={analysisSummary.calculateAbsoluteNdvi}
                onChange={(evt, v) => updateImagerySetup({ calculateAbsoluteNdvi: v })}
            />
        );
    };

    const getStartMonthOptions = () => {
        return monthNumbers.map((m) => {
            return {
                activeYn: true,
                label: m[1].toString(),
                value: m[0],
                sortOrder: Number(m[0]),
            };
        });
    };

    const getEndMonthOptions = () => {
        const unfilteredMonths = monthNumbers.map((m) => {
            return {
                activeYn: true,
                label: m[1].toString(),
                value: m[0],
                sortOrder: Number(m[0]),
            };
        });
        return unfilteredMonths.filter((m) => m.value >= analysisSummary.startMonth);
    };

    const isLoading = analysisSummary.isNew && !userMaxCloudPref && userMaxCloudPref !== 0;

    if (isLoading) return <Loader />;

    return (
        <div className="rec-event-info-form">
            {analysisSaveErrorMessages?.map((errorMessage) => (
                <div className="analysis-error-message" key={errorMessage}>
                    {errorMessage}
                </div>
            ))}
            <Bucket showSymbol={false} isCollapsible={false} isExpanded>
                <BucketHeader>{formatMessage(messages.analysisFormLabelText)}</BucketHeader>
                <div className="analysis-layer-imagery-setup-grid">
                    <TextInput
                        required={true}
                        autoFocus={!analysisSummary.name}
                        containerClassNames={["analysis-layer-imagery-setup-input"]}
                        onChange={(v) => updateImagerySetup({ name: v })}
                        placeholderText={formatMessage(messages.imagerySetupNamePlaceholder)}
                        value={analysisSummary.name}
                        maxLength={50}
                    />
                    <SelectInput
                        disabled={false}
                        clearable={false}
                        required={true}
                        containerClassNames={["analysis-layer-imagery-setup-input"]}
                        onChange={(v) =>
                            updateImagerySetup({
                                croppingSeasonGuid: v,
                                endMonth: null,
                                startMonth: null,
                            })
                        }
                        options={croppingSeasonOptions}
                        placeholderText={formatMessage(messages.season)}
                        value={analysisSummary.croppingSeasonGuid}
                    />
                    <SelectInput
                        disabled={false}
                        clearable={false}
                        required={true}
                        containerClassNames={["analysis-layer-imagery-setup-input"]}
                        onChange={(v) =>
                            updateImagerySetup({
                                startMonth: v,
                                endMonth: null,
                            })
                        }
                        options={getStartMonthOptions()}
                        placeholderText={formatMessage(messages.startMonthPlaceholder)}
                        value={analysisSummary.startMonth}
                    />
                    <SelectInput
                        disabled={!analysisSummary.startMonth}
                        clearable={false}
                        required={true}
                        containerClassNames={["analysis-layer-imagery-setup-input"]}
                        onChange={(v) =>
                            updateImagerySetup({
                                endMonth: v,
                            })
                        }
                        options={getEndMonthOptions()}
                        placeholderText={formatMessage(messages.endMonthPlaceholder)}
                        value={analysisSummary.endMonth}
                    />
                    <NumericInput
                        required={true}
                        autoFocus={!analysisSummary.maximumPercentCloudsAllowed}
                        containerClassNames={["analysis-layer-imagery-setup-input"]}
                        onChange={(v) => updateImagerySetup({ maximumPercentCloudsAllowed: v })}
                        placeholderText={formatMessage(
                            messages.maximumPercentCloudsAllowedPlaceholder
                        )}
                        value={analysisSummary.maximumPercentCloudsAllowed}
                        maxLength={3}
                        precision={3}
                        scale={0}
                    />
                    <SelectInput
                        disabled={!isNdviEnabled() || isStandardizedLegendChecked()}
                        clearable={false}
                        required={true}
                        containerClassNames={["analysis-layer-imagery-setup-input"]}
                        onChange={(v: string) => updateImagerySetup({ numClasses: v })}
                        options={numberOfClasses.map((x) => ({
                            value: x.toString(),
                            label: x.toString(),
                        }))}
                        placeholderText={formatMessage(messages.analysisNumClassesPlaceholderText)}
                        value={analysisSummary.numClasses.toString()}
                    />
                </div>
            </Bucket>
            <Bucket showSymbol={false} isCollapsible={false} isExpanded>
                <BucketHeader className="analysis-info-bucket-header">
                    {formatMessage(messages.imageryOptions)}
                </BucketHeader>
                {getTypeCheckboxes()}
            </Bucket>
            <Bucket showSymbol={false} isCollapsible={false} isExpanded>
                <BucketHeader className="analysis-info-bucket-header">
                    {formatMessage(messages.classification)}
                </BucketHeader>
                {getClassificationOptions()}
            </Bucket>
        </div>
    );
};

const mapDispatchToProps = (dispatch) => ({
    updateAnalysisSummary: (newProps) => dispatch(actions.updateImageryAnalysisSummary(newProps)),
    fetchPicklistData: (pickLists) => dispatch(picklistActions.fetchPicklistData(pickLists)),
});

const mapStateToProps = (state) => {
    const croppingSeasonOptions = picklistSelectors.getPicklistOptionsFromCode(
        state,
        picklistNames.getPickListCode(picklistNames.PICKLIST_CROPPING_SEASON)
    );
    const imageryTypeOptions = picklistSelectors.getPicklistOptionsFromCode(
        state,
        picklistNames.getPickListCode(picklistNames.PICKLIST_IMAGERY_TYPE)
    );
    const fieldGuid = getToolsetPayload(state).field?.fieldGuid;
    const zonesAdded = zonesHaveBeenAdded(state, fieldGuid);
    const analysisSaveErrorMessages = selectors.getAnalysisSaveErrorMessages(state);
    return {
        croppingSeasonOptions,
        imageryTypeOptions,
        zonesAdded,
        fieldGuid,
        analysisSaveErrorMessages,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(AnalysisImagerySetupForm));
